import { useRef, useState } from 'react';
import { Form, FormProps, Input } from 'antd';
import { useTranslate, TFnType } from '@tolgee/react';
import { isValidPhoneNumber } from 'libphonenumber-js/mobile';
import AgreementsCheckbox from 'bizComponents/AgreementsCheckbox';
import { phoneRegionCodesMap } from 'utils/phoneHelpers';
import productLine, { isQuickChat } from 'productLine';
import { css } from '@emotion/css';
import PhoneInput, { PhoneInputProps } from '../../views/Startups/components/PhoneInput';
import CaptchaInput from '../../views/Startups/components/CaptchaInput';
import { InviteVisibleControl } from 'hooks/InviteVisibleControl';
import { Help } from './Help';
import passwordValidator from '../../utils/validator/password';
import { useMemoizedFn } from 'ahooks';
import { useSMSCaptchaV2 } from '../SMSCaptchaButton/hooks/useSMSCaptchaV2';
import { CaptchaImageModal } from '../SMSCaptchaButton/CaptchaImageModal';

const { Item: FormItem } = Form;
export interface Values {
  countryCode: General.PhoneRegionCodes;
  mobile: `${number}`;
  captcha: string;
  agree: boolean;
  password: string;
  inviteCode?: string;
}

export interface MobileCaptchaFormProps extends Omit<FormProps<Values>, 'autoComplete'> {
  mode: 'reg' | 'login' | 'renew';
  autoComplete?: PhoneInputProps['autoComplete'];
  labelLess?: boolean;
  skipCaptcha?: boolean;
  children?: React.ReactNode;
  switchMode?: () => void;
}

const mobileValidate = async (
  mode: MobileCaptchaFormProps['mode'],
  mobile: string,
  t: TFnType,
  region: General.PhoneRegions,
  regionCode?: General.PhoneRegionCodes,
) => {
  let error;
  const combinedMobile = `${regionCode}${mobile}`;
  try {
    if (!mobile || !isValidPhoneNumber(combinedMobile)) {
      error = t?.('ViewStartups.registry.form.item.account.validator.error');
    }
    // else if (mode === 'reg') {
    //   const { registered } = await checkIsPhoneRegistered(combinedMobile);
    //   if (registered) {
    //     error = '该手机号已被注册，请更换其他手机号';
    //   }
    // }
  } catch (e) {
    error = '手机号校验失败，请稍后再试';
  }
  return error;
};

const MobileCaptchaForm = (props: MobileCaptchaFormProps) => {
  const {
    mode,
    autoComplete,
    labelLess: _labelLess = false,
    skipCaptcha = false,
    children = null,
    onValuesChange,
    switchMode,
    ...formProps
  } = props;
  const { t } = useTranslate();
  const valuesRef = useRef<Values>({ ...formProps.initialValues } as Values);

  const [country, setCountry] = useState<General.PhoneRegions>('cn');
  const labelLess = isQuickChat ? true : _labelLess;
  const helpRef = useRef<{ trigger: () => void }>(null!);

  const {
    captchaTimes,
    countdown,
    loading: captchaLoading,
    confirmLoading,
    captchaImage,
    sendSMSCaptchaByPhone,
    confirmCaptchaImage,
    modalVisible,
    closeModal,
  } = useSMSCaptchaV2({
    onNotResponse: () => {
      helpRef.current.trigger();
    },
  });

  const handleValuesChange = useMemoizedFn((changeValues: any, values: Values) => {
    valuesRef.current = { ...valuesRef.current, ...values };
    onValuesChange && onValuesChange(changeValues, values);
  });

  return (
    <>
      <Form
        layout="vertical"
        size="large"
        labelCol={{ span: 20 }}
        className={isQuickChat ? formCss : ''}
        validateTrigger="change"
        onValuesChange={handleValuesChange}
        {...formProps}
      >
        <>
          <FormItem name="countryCode" noStyle shouldUpdate />
          <FormItem dependencies={['mobile', 'countryCode']} noStyle>
            {({ setFieldsValue, validateFields }) => {
              return (
                <FormItem
                  name="mobile"
                  label={labelLess ? null : t('ViewStartups.registry.form.item.account.label')}
                  rules={[
                    ({ getFieldValue }) => ({
                      validator: async (_, mobile) => {
                        if (!mobile) return Promise.resolve();
                        const error = await mobileValidate(
                          mode,
                          mobile,
                          t,
                          country,
                          getFieldValue('countryCode'),
                        );
                        return error ? Promise.reject(new Error(error)) : Promise.resolve();
                      },
                    }),
                  ]}
                >
                  <PhoneInput
                    autoComplete={autoComplete}
                    phoneCode={phoneRegionCodesMap[country]}
                    onChange={(value, countryCode, region) => {
                      valuesRef.current = {
                        ...valuesRef.current,
                        mobile: value as `${number}`,
                        countryCode,
                      };
                      setFieldsValue({ mobile: value as `${number}`, countryCode });
                      validateFields(['mobile']);
                      setCountry(region);
                    }}
                  />
                </FormItem>
              );
            }}
          </FormItem>
          {skipCaptcha ? null : (
            <FormItem noStyle shouldUpdate>
              {({ getFieldValue, getFieldError, setFieldsValue }) => {
                const countryCode = getFieldValue('countryCode');
                const mobile = getFieldValue('mobile');
                const errors = getFieldError('mobile');
                const disabled = !mobile || errors?.length > 0;
                return (
                  <FormItem
                    className="[&_label]:w-full"
                    labelCol={{ span: 24 }}
                    label={
                      labelLess ? null : (
                        <div className="flex justify-between w-full">
                          <span>{t('短信验证码')}</span>
                          <Help
                            mode={mode === 'renew' ? undefined : mode}
                            ref={helpRef}
                            onOk={switchMode}
                          />
                        </div>
                      )
                    }
                    required={mode !== 'login'}
                    name="captcha"
                    rules={[{ required: true, message: t('ViewStartups.required') }]}
                    shouldUpdate
                  >
                    <CaptchaInput
                      onChange={(captcha) => {
                        setFieldsValue({ captcha });
                      }}
                      value={getFieldValue('captcha')}
                      loading={captchaLoading}
                      disabled={disabled}
                      onSendSMS={() => {
                        sendSMSCaptchaByPhone({
                          phoneNumber: `${countryCode}${mobile}`,
                          purpose: mode === 'login' ? 'verify' : 'register',
                        });
                      }}
                      cooldown={countdown}
                      times={captchaTimes}
                      defaultI18nKey="获取短信验证码"
                    />
                  </FormItem>
                );
              }}
            </FormItem>
          )}
          {mode === 'reg' && (
            <>
              <FormItem
                name="password"
                required
                label={labelLess ? null : t('ViewStartups.registry.form.item.password.label')}
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, pwd) => {
                      const email = getFieldValue('email');
                      const error = passwordValidator(pwd, email);
                      return !error.valid
                        ? Promise.reject(new Error(error.message))
                        : Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  type="password"
                  placeholder={t('ViewStartups.registry.form.item.password.validator.error.length')}
                />
              </FormItem>
              <FormItem
                name="repassword"
                required
                label={
                  labelLess ? null : t('ViewStartups.registry.form.item.confirm.password.label')
                }
                rules={[
                  ({ getFieldValue }) => ({
                    validator: async (_, repwd) => {
                      const pwd = getFieldValue('password');
                      if (!pwd) {
                        return Promise.resolve();
                      }
                      if (!repwd) {
                        return Promise.reject(
                          new Error(t('ViewStartups.reset.password.confirm.placeholder')),
                        );
                      }
                      if (pwd !== repwd) {
                        return Promise.reject(
                          new Error(t('ViewStartups.reset.form.password.validate.diff')),
                        );
                      }
                      return Promise.resolve();
                    },
                  }),
                ]}
              >
                <Input
                  type="password"
                  placeholder={t('ViewStartups.reset.password.confirm.placeholder')}
                />
              </FormItem>
            </>
          )}
          {mode === 'reg' ? (
            <>
              {productLine !== 'aeplugin' && !isQuickChat && (
                <InviteVisibleControl>
                  <FormItem
                    name="inviteCode"
                    label={t('ViewStartups.registry.info.form.invite.label')}
                  >
                    <Input placeholder={t('ViewStartups.registry.info.form.invite.placeholder')} />
                  </FormItem>
                </InviteVisibleControl>
              )}
              <FormItem name="agree" valuePropName="checked">
                <AgreementsCheckbox />
              </FormItem>
            </>
          ) : null}
          {children}
        </>
      </Form>
      <CaptchaImageModal
        visible={modalVisible}
        imgSrc={captchaImage}
        loading={confirmLoading}
        onConfirm={confirmCaptchaImage}
        onCancel={closeModal}
        onChangeImage={() => {
          confirmCaptchaImage('', true);
        }}
      />
    </>
  );
};

export default MobileCaptchaForm;

const formCss = css`
  .ant-form-item {
    margin-bottom: 16px !important;
    &:nth-last-of-type(1) {
      margin-bottom: 0 !important;
    }
  }
`;
